import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard, SectionCard, Money, Loader } from "@/components/common";
import { formatINR, timeAgo } from "@/lib/format";
import {
  IndianRupee, TrendingUp, Wallet, Landmark, ArrowDownCircle, PiggyBank,
  Boxes, Percent, Sparkles, ArrowRight, TriangleAlert, Package, Zap,
  Clock, Lightbulb, TrendingDown,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const CHART = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4"];
const ALERT_ICON = { package: Package, "trending-up": TrendingUp, percent: Percent, clock: Clock, "alert-triangle": TriangleAlert, zap: Zap, lightbulb: Lightbulb };
const ALERT_STYLE = {
  danger: "border-destructive/30 bg-destructive/5 text-destructive",
  warning: "border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5 text-[hsl(var(--warning))]",
  success: "border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 text-[hsl(var(--success))]",
  info: "border-primary/30 bg-primary/5 text-primary",
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-lg text-xs">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="capitalize text-muted-foreground">{p.name}:</span>
          <span className="font-semibold tabular-nums">{formatINR(p.value, { compact: true })}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((d) => setData(d.data)).catch(() => {});
    if (user?.role === "owner" || user?.role === "manager") {
      api.get("/alerts").then((a) => setAlerts(a.data)).catch(() => setAlerts([]));
    }
  }, [user]);

  if (!data) return <Loader label="Loading your command center…" />;
  const k = data.kpis;

  return (
    <div>
      <PageHeader
        title={`Good day, ${user?.name?.split(" ")[0]}`}
        subtitle={`Here's how your business is performing today · ${new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}`}
      >
        <Button onClick={() => navigate("/billing")} data-testid="new-sale-btn" className="font-semibold">
          <IndianRupee className="h-4 w-4 mr-1.5" /> New Sale
        </Button>
        <Button variant="outline" onClick={() => navigate("/advisor")} data-testid="dashboard-ai-btn" className="font-semibold">
          <Sparkles className="h-4 w-4 mr-1.5 text-primary" /> Ask AI
        </Button>
      </PageHeader>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="kpi-today-sales" index={0} label="Today's Sales" value={formatINR(k.today_sales)} icon={IndianRupee} accent="primary" />
        <KpiCard testid="kpi-month-sales" index={1} label="This Month" value={formatINR(k.month_sales, { compact: true })} delta={k.sales_growth} icon={TrendingUp} accent="success" />
        <KpiCard testid="kpi-net-profit" index={2} label="Net Profit (MTD)" value={formatINR(k.month_net_profit, { compact: true })} delta={k.profit_growth} icon={PiggyBank} accent={k.profit_growth >= 0 ? "success" : "danger"} />
        <KpiCard testid="kpi-margin" index={3} label="Profit Margin" value={`${k.month_margin}%`} delta={+(k.month_margin - k.prev_margin).toFixed(1)} deltaLabel="vs last month" icon={Percent} accent="warning" />
        <KpiCard testid="kpi-cash" index={4} label="Cash Available" value={formatINR(k.cash_available, { compact: true })} icon={Wallet} accent="success" />
        <KpiCard testid="kpi-bank" index={5} label="Bank / Online" value={formatINR(k.bank_balance, { compact: true })} icon={Landmark} accent="primary" />
        <KpiCard testid="kpi-receivable" index={6} label="Receivables" value={formatINR(k.receivables, { compact: true })} icon={ArrowDownCircle} accent="warning" />
        <KpiCard testid="kpi-inventory" index={7} label="Inventory Value" value={formatINR(k.inventory_value, { compact: true })} icon={Boxes} accent="primary" />
      </div>

      {/* AI insight banner */}
      <div
        data-testid="ai-insight-banner"
        onClick={() => navigate("/advisor")}
        className="mb-6 cursor-pointer group rounded-xl border border-primary/30 bg-primary/5 p-5 flex flex-col sm:flex-row sm:items-center gap-4 transition-colors hover:bg-primary/10 animate-fade-up"
      >
        <div className="grid place-items-center h-11 w-11 rounded-lg bg-primary text-primary-foreground shrink-0">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-semibold">AI Advisor spotted something</div>
          <p className="text-sm text-muted-foreground">
            {k.profit_growth < 0
              ? `Sales are ${k.sales_growth >= 0 ? "up" : "down"} ${Math.abs(k.sales_growth)}% but net profit fell ${Math.abs(k.profit_growth)}% — margin dropped from ${k.prev_margin}% to ${k.month_margin}%. Ask why.`
              : "Your business is trending well. Ask the AI what to double down on this month."}
          </p>
        </div>
        <ArrowRight className="h-5 w-5 text-primary group-hover:translate-x-1 transition-transform" />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Sales & Profit" subtitle="Last 14 days" className="lg:col-span-2" testid="chart-sales-trend">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.sales_trend} margin={{ left: 8, right: 8, top: 6 }}>
              <defs>
                <linearGradient id="gSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.5} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} interval={1} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => formatINR(v, { compact: true })} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="sales" stroke="#6366F1" strokeWidth={2.5} fill="url(#gSales)" />
              <Area type="monotone" dataKey="profit" stroke="#10B981" strokeWidth={2.5} fill="url(#gProfit)" />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Expense Breakdown" subtitle="This month" testid="chart-expense-breakdown">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.expense_breakdown} dataKey="value" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={2}>
                {data.expense_breakdown.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {data.expense_breakdown.slice(0, 4).map((e, i) => (
              <div key={e.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHART[i % CHART.length] }} />
                  {e.name}
                </span>
                <Money value={e.value} compact className="font-semibold" />
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Monthly + payment split */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Monthly Performance" subtitle="Sales · Expenses · Profit" className="lg:col-span-2" testid="chart-monthly">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.monthly_trend} margin={{ left: 8, right: 8 }} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.5} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => formatINR(v, { compact: true })} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
              <Bar dataKey="sales" fill="#6366F1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Payment Mix" subtitle="This month" testid="chart-payment-split">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.payment_split} dataKey="value" nameKey="name" outerRadius={80}>
                {data.payment_split.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {data.payment_split.map((e, i) => (
              <div key={e.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHART[i % CHART.length] }} />
                  {e.name}
                </span>
                <Money value={e.value} compact className="font-semibold" />
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Products + Alerts + transactions */}
      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard title="Best Sellers" subtitle="By revenue" testid="best-products">
          <div className="space-y-3">
            {data.best_products.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="grid place-items-center h-7 w-7 rounded-md bg-primary/10 text-primary text-xs font-bold">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.qty} units sold</div>
                </div>
                <Money value={p.revenue} compact className="text-sm font-semibold" />
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Slow Movers" subtitle="Capital stuck in stock" testid="slow-products">
          <div className="space-y-3">
            {data.slow_products.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <TrendingDown className="h-4 w-4 text-[hsl(var(--warning))] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">Idle {p.days_idle}d · {p.qty} in stock</div>
                </div>
                <Money value={p.value} compact className="text-sm font-semibold" />
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Live Alerts" subtitle={`${alerts.length} need attention`} testid="dashboard-alerts" action={<Button variant="ghost" size="sm" onClick={() => navigate("/alerts")}>View all</Button>}>
          <div className="space-y-2.5">
            {alerts.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No active alerts.</p>}
            {alerts.slice(0, 4).map((a, i) => {
              const Icon = ALERT_ICON[a.icon] || TriangleAlert;
              return (
                <div key={i} className={`flex gap-3 rounded-lg border p-3 ${ALERT_STYLE[a.type]}`}>
                  <Icon className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-bold">{a.title}</div>
                    <div className="text-xs opacity-90 mt-0.5 text-foreground/80">{a.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* Recent transactions */}
      <SectionCard title="Recent Transactions" className="mt-6" testid="recent-transactions" action={<Button variant="ghost" size="sm" onClick={() => navigate("/billing")}>Go to Billing</Button>}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-semibold py-2.5">Invoice</th>
                <th className="text-left font-semibold py-2.5">Customer</th>
                <th className="text-left font-semibold py-2.5 hidden sm:table-cell">Method</th>
                <th className="text-left font-semibold py-2.5 hidden sm:table-cell">Status</th>
                <th className="text-left font-semibold py-2.5">When</th>
                <th className="text-right font-semibold py-2.5">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_transactions.map((t, i) => (
                <tr key={i} className="border-b border-border/60 hover:bg-secondary/50 transition-colors">
                  <td className="py-3 font-medium">{t.invoice_number}</td>
                  <td className="py-3">{t.customer}</td>
                  <td className="py-3 hidden sm:table-cell"><Badge variant="secondary" className="uppercase text-[10px]">{t.method}</Badge></td>
                  <td className="py-3 hidden sm:table-cell">
                    <span className={`text-xs font-semibold ${t.status === "credit" ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--success))]"}`}>
                      {t.status === "credit" ? "On Credit" : "Paid"}
                    </span>
                  </td>
                  <td className="py-3 text-muted-foreground">{timeAgo(t.date)}</td>
                  <td className="py-3 text-right font-semibold tabular-nums">{formatINR(t.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
